"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useAuth } from "@/lib/auth-client";
import { Button } from "@/lib/components/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/lib/components/form";
import { Input } from "@/lib/components/input";
import { toast } from "@/lib/components/use-toast";

const formSchema = z.object({
	email: z.string().email({
		message: "Please enter a valid email address.",
	}),
});

export default function ForgotPassword() {
	const router = useRouter();
	const [isLoading, setIsLoading] = useState(false);
	const [emailSent, setEmailSent] = useState(false);
	const authClient = useAuth();
	const { theme } = useTheme();

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: "",
		},
	});

	async function onSubmit(values: z.infer<typeof formSchema>) {
		setIsLoading(true);

		try {
			const { error } = await authClient.forgetPassword({
				email: values.email,
				redirectTo: "/reset-password",
			});

			if (error) {
				toast({
					title: error.message || "Failed to send reset code",
					variant: "destructive",
				});
			} else {
				setEmailSent(true);
				toast({
					title: "Reset code sent!",
					description: "Check your email for the OTP code.",
				});
			}
		} catch (_error) {
			toast({
				title: "An error occurred",
				description: "Please try again later.",
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	}

	const logoSrc =
		theme === "dark" ? "/brand-mark-dark.png" : "/brand-mark-light.png";

	if (emailSent) {
		return (
			<div className="flex min-h-screen w-screen overflow-hidden">
				<div className="hidden flex-1 flex-col items-center justify-center bg-muted p-12 lg:flex relative">
					<div className="flex flex-col items-center gap-4">
						<Image src={logoSrc} alt="RouterBench" width={80} height={80} />
						<h1 className="text-3xl font-bold tracking-tight text-foreground">
							RouterBench
						</h1>
						<p className="text-muted-foreground text-center max-w-xs">
							One API for all your AI models. Build faster with unified access
							to 200+ models.
						</p>
					</div>
					<div className="absolute bottom-12 left-12 right-12 flex gap-8 border-t border-border pt-6">
						<div>
							<div className="text-lg font-bold text-foreground tracking-tight">
								200+
							</div>
							<div className="text-xs text-muted-foreground mt-0.5">
								AI Models
							</div>
						</div>
						<div>
							<div className="text-lg font-bold text-foreground tracking-tight">
								1,000+
							</div>
							<div className="text-xs text-muted-foreground mt-0.5">
								Developers
							</div>
						</div>
						<div>
							<div className="text-lg font-bold text-foreground tracking-tight">
								99.9%
							</div>
							<div className="text-xs text-muted-foreground mt-0.5">Uptime</div>
						</div>
					</div>
				</div>
				<div className="flex flex-1 items-center justify-center bg-background p-6 sm:p-12">
					<div className="w-full max-w-[380px]">
						<div className="flex items-center gap-2 mb-8">
							<Image src={logoSrc} alt="RouterBench" width={36} height={36} />
							<span className="text-xl font-semibold tracking-tight">
								RouterBench
							</span>
						</div>
						<div className="flex flex-col space-y-2 mb-6">
							<h1 className="text-2xl font-semibold tracking-tight">
								Check your email
							</h1>
							<p className="text-sm text-muted-foreground">
								We've sent a reset code to {form.getValues("email")}. Enter it
								on the reset password page.
							</p>
						</div>
						<Button
							size="lg"
							className="w-full mt-2"
							onClick={() => router.push("/reset-password")}
						>
							Continue to reset password
						</Button>
						<p className="text-center text-sm text-muted-foreground mt-8">
							<Link
								href="/login"
								className="hover:text-primary underline underline-offset-4"
							>
								Back to login
							</Link>
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="flex min-h-screen w-screen overflow-hidden">
			<div className="hidden flex-1 flex-col items-center justify-center bg-muted p-12 lg:flex relative">
				<div className="flex flex-col items-center gap-4">
					<Image src={logoSrc} alt="RouterBench" width={80} height={80} />
					<h1 className="text-3xl font-bold tracking-tight text-foreground">
						RouterBench
					</h1>
					<p className="text-muted-foreground text-center max-w-xs">
						One API for all your AI models. Build faster with unified access to
						200+ models.
					</p>
				</div>
				<div className="absolute bottom-12 left-12 right-12 flex gap-8 border-t border-border pt-6">
					<div>
						<div className="text-lg font-bold text-foreground tracking-tight">
							200+
						</div>
						<div className="text-xs text-muted-foreground mt-0.5">
							AI Models
						</div>
					</div>
					<div>
						<div className="text-lg font-bold text-foreground tracking-tight">
							1,000+
						</div>
						<div className="text-xs text-muted-foreground mt-0.5">
							Developers
						</div>
					</div>
					<div>
						<div className="text-lg font-bold text-foreground tracking-tight">
							99.9%
						</div>
						<div className="text-xs text-muted-foreground mt-0.5">Uptime</div>
					</div>
				</div>
			</div>
			<div className="flex flex-1 items-center justify-center bg-background p-6 sm:p-12">
				<div className="w-full max-w-[380px]">
					<div className="flex items-center gap-2 mb-8">
						<Image src={logoSrc} alt="RouterBench" width={36} height={36} />
						<span className="text-xl font-semibold tracking-tight">
							RouterBench
						</span>
					</div>
					<div className="flex flex-col space-y-2 mb-6">
						<h1 className="text-2xl font-semibold tracking-tight">
							Forgot your password?
						</h1>
						<p className="text-sm text-muted-foreground">
							Enter your email and we'll send you a reset code
						</p>
					</div>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm font-medium text-foreground">
											Email
										</FormLabel>
										<FormControl>
											<Input
												placeholder="name@example.com"
												type="email"
												autoComplete="email"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Button
								type="submit"
								size="lg"
								className="w-full mt-2"
								disabled={isLoading}
							>
								{isLoading ? (
									<>
										<Loader2 className="size-4 mr-2 animate-spin" />
										Sending code...
									</>
								) : (
									"Send reset code"
								)}
							</Button>
						</form>
					</Form>
					<p className="text-center text-sm text-muted-foreground mt-8">
						<Link
							href="/login"
							className="hover:text-primary underline underline-offset-4"
						>
							Back to login
						</Link>
					</p>
				</div>
			</div>
		</div>
	);
}
